import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Edit, Delete, Add, ArrowBack, Search, FilterList, FactCheck } from "@mui/icons-material";
import styles from '../../assets/styles/LayoutShared.module.css';
import api from '../../services/api';
import { formatarDataBR } from '../../utils/formatters'; // Usando sua função global corrigida
import RegistroForm from './RegistroForm';
import { descricaoSemLocal, PARAM_FILTROS_IGNORADOS, PendenciaRegistro } from '../../utils/registrosPendencias';

// Status de validação nunca é armazenado — é sempre calculado cruzando
// tbl_pesca_projeto.exige_validacao com tbl_pesca_registro.data_validacao
// (mesma regra usada em todas as telas que mostram esse status). Assim, um
// projeto que passa a exigir validação já mostra os registros antigos como
// pendentes automaticamente, sem precisar de nenhuma migração retroativa.
type StatusValidacao = 'pendente' | 'validado' | 'nao_exige';

function calcularStatusValidacao(registro: any): StatusValidacao {
  if (!registro.projeto?.exige_validacao) return 'nao_exige';
  return registro.data_validacao ? 'validado' : 'pendente';
}

const STATUS_VALIDACAO_INFO: Record<StatusValidacao, { cor: string; label: string }> = {
  pendente: { cor: '#e08a00', label: 'Pendente de validação' },
  validado: { cor: '#00b37e', label: 'Validado' },
  nao_exige: { cor: '#bbb', label: 'Este projeto não exige validação' },
};

// 'producao' nunca ganha selo — mesmo mapa/estilo de badge de RegistroView.tsx.
const DATA_SCOPE_LABEL: Record<string, string> = {
  demonstracao: 'Demonstração',
  teste: 'Teste',
};

const RegistrosList = () => {
  const navigate = useNavigate();
  // Vindo do Dashboard (clique em "Sem local vinculado"), a URL já traz pendência,
  // projeto e período; lido só na montagem pra semear os filtros.
  const [paramsIniciais] = useSearchParams();
  const isAdmin = localStorage.getItem('modulo') === 'admin';

  // Estados para dados e paginação
  const [registros, setRegistros] = useState<any[]>([]);
  const [pagina, setPagina] = useState(1);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [carregando, setCarregando] = useState(false);
  const [erroCarga, setErroCarga] = useState<string | null>(null);
  const temMais = registros.length < totalRegistros;

  // Só a busca mais recente vale: ao trocar filtro/página a anterior é
  // cancelada (AbortController) e, se ainda assim chegar, é descartada pelo
  // contador. Sem isso, cliques repetidos empilham requisições (cada uma presa
  // até 30 s num backend lento) e respostas antigas se misturam à lista.
  const abortRef = useRef<AbortController | null>(null);
  const buscaAtualRef = useRef(0);

  // Estados para pesquisa com Debounce
  const [tempSearch, setTempSearch] = useState("");
  const [search, setSearch] = useState("");

  // Opções dos selects de filtro — os três endpoints já vêm restritos ao(s)
  // projeto(s) do usuário logado (getProjetosPermitidos no backend), não
  // precisa de nenhuma lógica extra aqui pra esconder opção.
  const [projetos, setProjetos] = useState<any[]>([]);
  const [areas, setAreas] = useState<any[]>([]);
  const [pescadores, setPescadores] = useState<any[]>([]);

  const pendenciaInicial = paramsIniciais.get('pendencia');
  const [filtroPendencia, setFiltroPendencia] = useState<PendenciaRegistro>(
    pendenciaInicial === 'sem_local' || pendenciaInicial === 'nao_validados' ? pendenciaInicial : ''
  );
  // Contagem ao lado de cada opção de Pendências (vem na 1ª página do back).
  const [contagens, setContagens] = useState<{ todos: number; sem_local: number; nao_validados: number } | null>(null);
  const avisoFiltrosDashboard = paramsIniciais.get(PARAM_FILTROS_IGNORADOS) === '1';
  const [filtrosAbertos, setFiltrosAbertos] = useState(
    !!(pendenciaInicial || paramsIniciais.get('uid_pesca_projeto') || paramsIniciais.get('uid_pesca_projetos') || paramsIniciais.get('data_inicio_de'))
  );
  // Um uid (menu / select) ou vários separados por vírgula (vindo do Dashboard com mais de um projeto).
  const [filtroProjeto, setFiltroProjeto] = useState(paramsIniciais.get('uid_pesca_projetos') || paramsIniciais.get('uid_pesca_projeto') || "");
  const [filtroArea, setFiltroArea] = useState("");
  const [filtroPescador, setFiltroPescador] = useState("");
  const [filtroDataInicioDe, setFiltroDataInicioDe] = useState(paramsIniciais.get('data_inicio_de') || "");
  const [filtroDataInicioAte, setFiltroDataInicioAte] = useState(paramsIniciais.get('data_inicio_ate') || "");
  // Registros demo/teste (Laboratório de Interação) ficam ocultos por
  // padrão nesta lista (aplicarFiltroDataScope no backend) — esse
  // checkbox é o único jeito de revelá-los, via ?incluirDemo=true.
  const [incluirDemo, setIncluirDemo] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [registroEditandoUid, setRegistroEditandoUid] = useState<string | null>(null);

  useEffect(() => {
    api.get('/pesca/projetos').then(res => setProjetos(res.data.data));
    api.get('/pesca/areas').then(res => setAreas(res.data.data));
    // 16 pescadores hoje, mesmo já restrito por projeto — cabe tranquilo num
    // select simples, sem precisar de autocomplete (ver PescadorForm/Município
    // pro padrão de autocomplete, caso a lista cresça muito no futuro).
    api.get('/pesca/pescadores?limit=1000').then(res => setPescadores(res.data.data));
  }, []);

  // Busca dados do backend com paginação, busca textual e filtros
  const fetchRegistros = useCallback((
    pageParam: number,
    searchTerm: string,
    filtros: { projeto: string; area: string; pescador: string; dataDe: string; dataAte: string; incluirDemo: boolean; pendencia: PendenciaRegistro },
    isNewSearch: boolean
  ) => {
    const params = new URLSearchParams({ page: String(pageParam), search: searchTerm, limit: '20' });
    if (filtros.projeto) params.set(filtros.projeto.includes(',') ? 'uid_pesca_projetos' : 'uid_pesca_projeto', filtros.projeto);
    if (filtros.area) params.set('uid_pesca_area', filtros.area);
    if (filtros.pescador) params.set('uid_pesca_pescador', filtros.pescador);
    if (filtros.dataDe) params.set('data_inicio_de', filtros.dataDe);
    if (filtros.dataAte) params.set('data_inicio_ate', filtros.dataAte);
    if (filtros.incluirDemo) params.set('incluirDemo', 'true');
    if (filtros.pendencia) params.set('pendencia', filtros.pendencia);

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const busca = ++buscaAtualRef.current;
    setCarregando(true);
    setErroCarga(null);

    api.get(`/pesca/registros?${params.toString()}`, { signal: controller.signal })
      .then(res => {
        if (busca !== buscaAtualRef.current) return;
        const novosDados = res.data.data;
        setRegistros(prev => (isNewSearch ? novosDados : [...prev, ...novosDados]));
        setTotalRegistros(res.data.total || 0);
        if (res.data.contagens) setContagens(res.data.contagens);
      })
      .catch(err => {
        if (axios.isCancel(err) || busca !== buscaAtualRef.current) return;
        setErroCarga(err?.response?.data?.message || 'Não foi possível carregar os registros.');
      })
      .finally(() => {
        if (busca === buscaAtualRef.current) setCarregando(false);
      });
  }, []);

  // Ao sair da tela, cancela a busca em andamento.
  useEffect(() => () => abortRef.current?.abort(), []);

  const filtrosAtuais = {
    projeto: filtroProjeto,
    area: filtroArea,
    pescador: filtroPescador,
    dataDe: filtroDataInicioDe,
    dataAte: filtroDataInicioAte,
    incluirDemo,
    pendencia: filtroPendencia,
  };

  // Efeito para Debounce da pesquisa: espera 0.5s após parar de digitar
  useEffect(() => {
    const handler = setTimeout(() => {
      setSearch(tempSearch);
    }, 500);
    return () => clearTimeout(handler);
  }, [tempSearch]);

  // Efeito que dispara a busca quando a busca, filtros ou página mudam
  useEffect(() => {
    setPagina(1);
    fetchRegistros(1, search, filtrosAtuais, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, filtroProjeto, filtroArea, filtroPescador, filtroDataInicioDe, filtroDataInicioAte, incluirDemo, filtroPendencia, fetchRegistros]);

  // Carregar mais
  useEffect(() => {
    if (pagina > 1) fetchRegistros(pagina, search, filtrosAtuais, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagina, fetchRegistros]);

  const limparFiltros = () => {
    setFiltroProjeto("");
    setFiltroArea("");
    setFiltroPescador("");
    setFiltroDataInicioDe("");
    setFiltroDataInicioAte("");
    setIncluirDemo(false);
    setFiltroPendencia("");
  };

  const qtdFiltrosAtivos = [filtroProjeto, filtroArea, filtroPescador, filtroDataInicioDe, filtroDataInicioAte, incluirDemo, filtroPendencia].filter(Boolean).length;

  const handleNovo = () => {
    setRegistroEditandoUid(null);
    setFormOpen(true);
  };

  const handleEditar = (uid: string) => {
    setRegistroEditandoUid(uid);
    setFormOpen(true);
  };

  const handleExcluir = async (r: any) => {
    if (!window.confirm('Remover este registro de pescaria?')) return;
    await api.delete(`/pesca/registros/${r.uid_pesca_registro}`);
    fetchRegistros(1, search, filtrosAtuais, true);
    setPagina(1);
  };

  const handleSalvo = () => {
    setFormOpen(false);
    fetchRegistros(1, search, filtrosAtuais, true);
    setPagina(1);
  };

  return (
    <div className={styles.container}>
      <div className={styles.headerTitle}>
        <h1 className={styles.greenTitle}>Registros de Pesca</h1>

        {/* Barra de Pesquisa posicionada entre Título e Botões */}
        <div style={{ position: 'relative', flex: 1, maxWidth: '400px', margin: '0 20px' }}>
          <Search style={{ position: 'absolute', left: '10px', top: '10px', color: '#888' }} />
          <input
            type="text"
            placeholder="Pesquisar pescador ou local..."
            value={tempSearch}
            onChange={(e) => setTempSearch(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 10px 10px 40px',
              borderRadius: '20px',
              border: '1px solid #ddd',
              outline: 'none'
            }}
          />
        </div>

        <div className={styles.actionsHeader}>
          {/* Label Informativo de Quantidade */}
          <p style={{ alignSelf: 'center', fontSize: '0.9rem', color: '#666', marginRight: '10px' }}>
            Qtd: <span style={{ fontWeight: 'bold', color: 'var(--green-700)' }}>{totalRegistros}</span>
          </p>

          <button
            className={styles.blueBtn}
            onClick={() => setFiltrosAbertos(f => !f)}
            title="Filtros"
            style={{ position: 'relative' }}
          >
            <FilterList />
            {qtdFiltrosAtivos > 0 && (
              <span style={{
                position: 'absolute', top: -4, right: -4, background: '#f75a68', color: '#fff',
                borderRadius: '50%', width: 16, height: 16, fontSize: '0.65rem',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                {qtdFiltrosAtivos}
              </span>
            )}
          </button>

          <button className={styles.blueBtn} onClick={() => navigate('validar')} title="A Validar"><FactCheck /></button>
          <button className={styles.blueBtn} onClick={handleNovo} title="Adicionar Novo"><Add /></button>
          <button className={styles.blueBtn} onClick={() => navigate(-1)} title="Voltar"><ArrowBack /></button>
        </div>
      </div>

      {avisoFiltrosDashboard && (
        <p style={{ margin: '0 0 10px', fontSize: '0.8rem', color: '#8a6d00' }}>
          Alguns filtros do Dashboard não foram aplicados
        </p>
      )}

      {filtrosAbertos && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '16px' }}>
          <div role="group" aria-label="Pendências" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: '0.8rem', color: '#666' }}>Pendências:</span>
            {([
              ['', 'Todos', contagens?.todos],
              ['sem_local', 'Sem local vinculado', contagens?.sem_local],
              ['nao_validados', 'Não validados', contagens?.nao_validados],
            ] as [PendenciaRegistro, string, number | undefined][]).map(([valor, rotulo, qtd]) => {
              const ativo = filtroPendencia === valor;
              return (
                <button
                  key={valor || 'todos'}
                  type="button"
                  aria-pressed={ativo}
                  onClick={() => setFiltroPendencia(valor)}
                  style={{
                    padding: '8px 14px', borderRadius: '20px', cursor: 'pointer',
                    border: `1px solid ${ativo ? 'var(--green-700)' : '#ddd'}`,
                    background: ativo ? 'var(--green-700)' : '#fff', color: ativo ? '#fff' : '#444',
                  }}
                >
                  {rotulo}{qtd !== undefined ? ` (${qtd})` : ''}
                </button>
              );
            })}
            {isAdmin && filtroPendencia === 'sem_local' && (
              <button
                type="button"
                onClick={() => navigate('/admin/locais/pendencias')}
                style={{ padding: '8px 14px', borderRadius: '20px', border: '1px solid #03c9fe', background: '#fff', color: '#0a6c8f', cursor: 'pointer' }}
              >
                Resolver em lote
              </button>
            )}
          </div>

          <select
            value={filtroProjeto}
            onChange={(e) => setFiltroProjeto(e.target.value)}
            style={filterSelectStyle}
          >
            <option value="">Todos os projetos</option>
            {filtroProjeto.includes(',') && (
              <option value={filtroProjeto}>{filtroProjeto.split(',').length} projetos (do Dashboard)</option>
            )}
            {projetos.map((p) => (
              <option key={p.uid_pesca_projeto} value={p.uid_pesca_projeto}>{p.nome}</option>
            ))}
          </select>

          <select
            value={filtroArea}
            onChange={(e) => setFiltroArea(e.target.value)}
            style={filterSelectStyle}
          >
            <option value="">Todas as áreas</option>
            {areas.map((a) => (
              <option key={a.uid_pesca_area} value={a.uid_pesca_area}>{a.nome}</option>
            ))}
          </select>

          <select
            value={filtroPescador}
            onChange={(e) => setFiltroPescador(e.target.value)}
            style={filterSelectStyle}
          >
            <option value="">Todos os pescadores</option>
            {pescadores.map((p) => (
              <option key={p.uid_pesca_pescador} value={p.uid_pesca_pescador}>{p.nome}</option>
            ))}
          </select>

          <input
            type="date"
            value={filtroDataInicioDe}
            onChange={(e) => setFiltroDataInicioDe(e.target.value)}
            title="Data início (de)"
            style={filterSelectStyle}
          />
          <input
            type="date"
            value={filtroDataInicioAte}
            onChange={(e) => setFiltroDataInicioAte(e.target.value)}
            title="Data início (até)"
            style={filterSelectStyle}
          />

          <label style={{ ...filterSelectStyle, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={incluirDemo}
              onChange={(e) => setIncluirDemo(e.target.checked)}
            />
            Mostrar demonstração/teste
          </label>

          {qtdFiltrosAtivos > 0 && (
            <button
              onClick={limparFiltros}
              style={{ padding: '8px 16px', borderRadius: '20px', border: '1px solid #ddd', background: '#fff', color: '#666', cursor: 'pointer' }}
            >
              Limpar filtros
            </button>
          )}
        </div>
      )}

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th style={{ textAlign: 'center' }}>Validação</th>
              <th>Data</th>
              <th>Pescador</th>
              <th>Local/Projeto</th>
              <th>Peso Total (kg)</th>
              <th style={{ textAlign: 'center' }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {registros.map((r) => {
              const status = calcularStatusValidacao(r);
              const { cor, label } = STATUS_VALIDACAO_INFO[status];
              return (
                <tr key={r.uid_pesca_registro} onClick={() => navigate(`view/${r.uid_pesca_registro}`)} style={{ cursor: 'pointer' }}>
                  <td style={{ textAlign: 'center' }} title={label}>
                    <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: '50%', background: cor }} />
                  </td>
                  <td>
                    {formatarDataBR(r.data_inicio)}
                    {DATA_SCOPE_LABEL[r.data_scope] && (
                      <span style={{
                        marginLeft: 8, fontSize: '0.68rem', fontWeight: 700, color: '#8a3b00',
                        background: '#ffe4cc', padding: '2px 8px', borderRadius: 10, textTransform: 'uppercase',
                      }}>
                        {DATA_SCOPE_LABEL[r.data_scope]}
                      </span>
                    )}
                  </td>
                  <td>{r.pescador?.nome || 'Não identificado'}</td>
                  <td>
                    {r.projeto?.nome || '-'}
                    {' '}
                    {r.uid_pesca_local_tbl_pesca_local ? `(${r.uid_pesca_local_tbl_pesca_local.nome})` : (() => {
                      const { texto, selo } = descricaoSemLocal(r);
                      return (
                        <>
                          {texto && `(${texto})`}
                          <span style={{
                            marginLeft: 6, fontSize: '0.68rem', fontWeight: 700, color: '#8a3b00',
                            background: '#ffe4cc', padding: '2px 8px', borderRadius: 10,
                          }}>
                            {selo}
                          </span>
                        </>
                      );
                    })()}
                  </td>
                  <td style={{ fontWeight: 'bold' }}>
                    {(() => {
                      const peso = parseFloat(r.dataValues?.peso_calculado ?? r.peso_calculado ?? 0);
                      const quantidade = parseFloat(r.dataValues?.quantidade_calculada ?? r.quantidade_calculada ?? 0);
                      // Captura por peso OU por quantidade — se não tem peso
                      // nenhum mas tem quantidade, mostra em unidades em vez
                      // de "0.00 kg" (que parecia um registro vazio).
                      if (peso === 0 && quantidade > 0) return `${quantidade} un`;
                      return `${peso.toFixed(2)} kg`;
                    })()}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }} onClick={(e) => e.stopPropagation()}>
                      <Edit fontSize="small" style={{ color: '#03c9fe', cursor: 'pointer' }} onClick={() => handleEditar(r.uid_pesca_registro)} />
                      <Delete fontSize="small" style={{ color: '#f75a68', cursor: 'pointer' }} onClick={() => handleExcluir(r)} />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Controle de Paginação: Carregar Mais */}
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px', paddingBottom: '20px' }}>
        {erroCarga && (
          <p role="alert" style={{ color: '#c0392b', fontSize: '0.9rem', marginRight: 12, alignSelf: 'center' }}>{erroCarga}</p>
        )}
        {temMais ? (
          <button
            className={styles.blueBtn}
            style={{ width: 'auto', padding: '0 20px', opacity: carregando ? 0.6 : 1, cursor: carregando ? 'default' : 'pointer' }}
            disabled={carregando}
            onClick={() => { if (!carregando) setPagina(prev => prev + 1); }}
          >
            {carregando ? 'Carregando...' : 'Carregar Mais registros'}
          </button>
        ) : carregando ? (
          <p style={{ color: '#999', fontSize: '0.9rem' }}>Carregando...</p>
        ) : (
          <p style={{ color: '#999', fontSize: '0.9rem' }}>Todos os registros foram carregados</p>
        )}
      </div>

      {formOpen && (
        <RegistroForm
          registroUid={registroEditandoUid}
          onClose={() => setFormOpen(false)}
          onSaved={handleSalvo}
        />
      )}
    </div>
  );
};

const filterSelectStyle: React.CSSProperties = {
  padding: '8px 12px', borderRadius: '20px', border: '1px solid #ddd', background: '#fff',
};

export default RegistrosList;
